"use client";

import { useRouter } from "next/navigation";
import styled from "styled-components";

const Sidebar = styled.div`
  width: 280px;
  flex-shrink: 0;

  @media (max-width: 968px) {
    width: 100%;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
`;

const QuickAction = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  padding: 1rem;
  background: ${(props) => (props.$disabled ? "#e0e0e0" : "#c8553d")};
  color: ${(props) => (props.$disabled ? "#999" : "white")};
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  opacity: ${(props) => (props.$disabled ? 0.6 : 1)};
  transition: all 0.3s;

  &:hover {
    background: ${(props) => (props.$disabled ? "#e0e0e0" : "#b04731")};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const DisabledTooltip = styled.div`
  font-size: 0.75rem;
  color: #c8553d;
  text-align: center;
  margin-top: -0.5rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

interface CustomerSidebarProps {
  isArtisan?: boolean;
  checkingArtisan?: boolean;
  stripeConnected?: boolean;
  currentPage?: string; // Add current page context
}

export default function CustomerSidebar({
  isArtisan = false,
  checkingArtisan = false,
  stripeConnected = true,
  currentPage = "",
}: CustomerSidebarProps) {
  const router = useRouter();

  // For artisans on orders page, show simplified quick actions
  const isArtisanOnOrdersPage = isArtisan && currentPage === "orders";

  return (
    <Sidebar>
      <Section>
        <SectionHeader>
          <SectionTitle>Quick Actions</SectionTitle>
        </SectionHeader>

        <QuickAction onClick={() => router.push("/feed")}>
          🍽️ Browse Meals
        </QuickAction>

        {!isArtisanOnOrdersPage && (
          <QuickAction onClick={() => router.push("/orders")}>
            📦 My Orders
          </QuickAction>
        )}

        {!checkingArtisan && isArtisan && (
          <>
            {!isArtisanOnOrdersPage && (
              <>
                <QuickAction
                  $disabled={!stripeConnected}
                  onClick={() => {
                    if (stripeConnected) {
                      router.push("/artisan-dashboard/meals/new");
                    }
                  }}
                  title={!stripeConnected ? "Link your bank account first" : ""}
                >
                  ➕ Add New Meal
                </QuickAction>
                {!stripeConnected && (
                  <DisabledTooltip>Bank account required</DisabledTooltip>
                )}
              </>
            )}
            <QuickAction onClick={() => router.push("/artisan-dashboard")}>
              👨‍🍳 Artisan Dashboard
            </QuickAction>
          </>
        )}
      </Section>
    </Sidebar>
  );
}
